import { useCallback, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Todo } from '../types/todo';
import * as db from '../services/dbService';
import { queryKeys } from '../lib/queryKeys';
import { useAuth } from '../context/AuthContext';
import { randomId } from '../utils/idUtils';
import { computeNewChildren, filterTodosForDisplay } from '../utils/recurrenceUtils';
import { monthDateRange, toLocalDateStr } from '../utils/dateUtils';

export function useTodos(date: string) {
  const { userId } = useAuth();
  const qc = useQueryClient();
  const year = Number(date.slice(0, 4));
  const monthIndex = Number(date.slice(5, 7)) - 1;
  const { from, to } = monthDateRange(year, monthIndex);
  const key = queryKeys.todos(userId, date.slice(0, 7));

  const { data: allTodos = [], isLoading } = useQuery({
    queryKey: key,
    queryFn: () => db.fetchTodos({ from, to, includeDeleted: true }),
    staleTime: 30_000,
    enabled: !!userId,
  });

  // Compute visible todos for `date`, and trigger DB expansion for new recurring children
  const todos = useMemo(() => {
    const newChildren = computeNewChildren(allTodos, date);
    if (newChildren.length > 0) {
      // Optimistically add to cache, then persist
      qc.setQueryData<Todo[]>(key, prev => [...(prev ?? []), ...newChildren]);
      Promise.all(newChildren.map(c => db.upsertTodo(c)));
    }
    return filterTodosForDisplay([...allTodos, ...newChildren], date);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allTodos, date]);

  const upsert = useMutation({
    mutationFn: (todo: Todo) => db.upsertTodo(todo),
    onMutate: async (todo) => {
      await qc.cancelQueries({ queryKey: key });
      const prev = qc.getQueryData<Todo[]>(key) ?? [];
      const idx = prev.findIndex(t => t.id === todo.id);
      qc.setQueryData(key, idx >= 0
        ? prev.map(t => t.id === todo.id ? todo : t)
        : [...prev, todo]);
      return { prev };
    },
    onError: (_e, _v, ctx) => { if (ctx?.prev) qc.setQueryData(key, ctx.prev); },
  });

  const remove = useMutation({
    mutationFn: db.deleteTodo,
    onMutate: async (id) => {
      await qc.cancelQueries({ queryKey: key });
      const prev = qc.getQueryData<Todo[]>(key) ?? [];
      qc.setQueryData(key, prev.filter(t => t.id !== id));
      return { prev };
    },
    onError: (_e, _v, ctx) => { if (ctx?.prev) qc.setQueryData(key, ctx.prev); },
  });

  const removeSeries = useMutation({
    mutationFn: db.deleteTodoSeries,
    onMutate: async (parentId) => {
      await qc.cancelQueries({ queryKey: key });
      const prev = qc.getQueryData<Todo[]>(key) ?? [];
      qc.setQueryData(key, prev.filter(t => t.id !== parentId && t.recurrenceParentId !== parentId));
      return { prev };
    },
    onError: (_e, _v, ctx) => { if (ctx?.prev) qc.setQueryData(key, ctx.prev); },
  });

  const addTodo = useCallback(
    (data: Pick<Todo, 'title' | 'scheduledDate' | 'scheduledTime' | 'onComplete' | 'recurrenceRule'> & { sortOrder?: number }) => {
      const now = new Date().toISOString();
      const id = randomId();
      const initialOccurrenceId = data.recurrenceRule ? randomId() : undefined;

      const todo: Todo = {
        id,
        title: data.title,
        completed: false,
        completedAt: null,
        onComplete: data.onComplete,
        scheduledDate: data.scheduledDate,
        scheduledTime: data.scheduledTime,
        recurrenceRule: data.recurrenceRule,
        recurrenceParentId: null,
        sortOrder: data.sortOrder ?? 0,
        createdAt: now,
        updatedAt: now,
      };

      const toUpsert: Todo[] = [todo];

      // For recurring, also create today's child
      if (data.recurrenceRule && initialOccurrenceId) {
        toUpsert.push({
          ...todo,
          id: initialOccurrenceId,
          recurrenceRule: null,
          recurrenceParentId: id,
        });
      }

      // Optimistic update
      qc.setQueryData<Todo[]>(key, prev => [...(prev ?? []), ...toUpsert]);

      return db.upsertTodo(todo, initialOccurrenceId ? { initialOccurrenceId } : undefined);
    },
    [key, qc],
  );

  const toggleComplete = useCallback(
    (id: string) => {
      const all = qc.getQueryData<Todo[]>(key) ?? [];
      const todo = all.find(t => t.id === id);
      if (!todo) return;

      const nowCompleting = !todo.completed;

      if (nowCompleting && todo.onComplete === 'delete') {
        if (todo.recurrenceParentId) {
          upsert.mutate({ ...todo, deleted: true, updatedAt: new Date().toISOString() });
        } else {
          remove.mutate(id);
        }
      } else {
        upsert.mutate({
          ...todo,
          completed: nowCompleting,
          completedAt: nowCompleting ? new Date().toISOString() : null,
          updatedAt: new Date().toISOString(),
        });
      }
    },
    [key, qc, upsert, remove],
  );

  const updateTodo = useCallback(
    (todo: Todo) => {
      const now = new Date().toISOString();
      const all = qc.getQueryData<Todo[]>(key) ?? [];
      const prev = all.find((t) => t.id === todo.id);
      const next = { ...todo, updatedAt: now };
      const addingSeries =
        Boolean(next.recurrenceRule) &&
        !next.recurrenceParentId &&
        prev &&
        !prev.recurrenceRule &&
        !prev.recurrenceParentId;

      if (addingSeries) {
        const templateId = randomId();
        const template: Todo = {
          ...next,
          id: templateId,
          completed: false,
          completedAt: null,
          recurrenceParentId: null,
          updatedAt: now,
        };
        const occurrence: Todo = {
          ...next,
          recurrenceRule: null,
          recurrenceParentId: templateId,
          updatedAt: now,
        };
        qc.setQueryData<Todo[]>(key, (list) => {
          const rest = (list ?? []).filter((t) => t.id !== next.id && t.id !== templateId);
          return [...rest, template, occurrence];
        });
        void db.upsertTodo(template).then(() => db.upsertTodo(occurrence));
        return;
      }

      if (prev?.recurrenceParentId && next.recurrenceRule) {
        const parent = all.find((t) => t.id === prev.recurrenceParentId);
        upsert.mutate({
          ...next,
          recurrenceRule: null,
          recurrenceParentId: prev.recurrenceParentId,
        });
        if (parent) {
          upsert.mutate({
            ...parent,
            title: next.title,
            scheduledTime: next.scheduledTime,
            recurrenceRule: next.recurrenceRule,
            updatedAt: now,
          });
        }
        return;
      }

      upsert.mutate(next);
    },
    [key, qc, upsert],
  );

  const removeTodo = useCallback(
    (id: string) => {
      const all = qc.getQueryData<Todo[]>(key) ?? [];
      const todo = all.find(t => t.id === id);
      if (!todo) return;

      if (todo.recurrenceParentId) {
        // Soft-delete: mark deleted so expansion won't recreate it
        upsert.mutate({ ...todo, deleted: true, updatedAt: new Date().toISOString() });
      } else {
        remove.mutate(id);
      }
    },
    [key, qc, upsert, remove],
  );

  const updateTodoTime = useCallback(
    (id: string, scheduledTime: string | null) => {
      const all = qc.getQueryData<Todo[]>(key) ?? [];
      const todo = all.find(t => t.id === id);
      if (!todo) return;
      upsert.mutate({ ...todo, scheduledTime, updatedAt: new Date().toISOString() });
    },
    [key, qc, upsert],
  );

  return {
    todos,
    allTodos,
    isLoading,
    addTodo,
    updateTodo,
    toggleComplete,
    removeTodo,
    removeSeries: (parentId: string) => removeSeries.mutate(parentId),
    updateTodoTime,
  };
}

/** Utility – used by DailyTimeline and TodoList */
export function todayStr(): string {
  return toLocalDateStr(new Date());
}
